import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Check, X, Archive, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { expenseCategoriesApi } from "@/lib/api";

// Global category management dialog — Add / Edit / Delete or Archive.
// Categories that are already referenced by an expense are soft-deleted
// (archived); untouched categories are hard-deleted.
export function CategoriesDialog({ open, onOpenChange, categories, onChanged }) {
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [busyId, setBusyId] = useState(null);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await expenseCategoriesApi.create(name);
      toast.success(`Added "${name}"`);
      setNewName("");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const handleRename = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    setBusyId(id);
    try {
      await expenseCategoriesApi.update(id, { name });
      toast.success("Renamed");
      setEditingId(null);
      setEditingName("");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (cat) => {
    if (
      !window.confirm(
        `Delete "${cat.name}"? Categories in use will be archived (past expenses keep their history).`
      )
    )
      return;
    setBusyId(cat.id);
    try {
      const res = await expenseCategoriesApi.remove(cat.id);
      toast.success(res.archived ? `Archived "${cat.name}"` : `Deleted "${cat.name}"`);
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="categories-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Manage categories
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Categories are shared across every project. Renames cascade to historical
            expenses so reports stay legible.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 pt-2">
          <Input
            data-testid="new-category-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name"
            className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
          <Button
            data-testid="add-category-btn"
            onClick={handleAdd}
            disabled={!newName.trim() || adding}
            className="bg-amber-500 text-black hover:bg-amber-400"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>

        <ul className="mt-4 divide-y divide-zinc-800/70 border border-zinc-800 rounded-md">
          {categories.length === 0 ? (
            <li className="p-4 text-center text-sm text-zinc-500 italic">
              No categories yet.
            </li>
          ) : (
            categories.map((c) => (
              <li
                key={c.id}
                data-testid={`category-row-${c.id}`}
                className="flex items-center gap-2 p-3 hover:bg-zinc-900/40"
              >
                {editingId === c.id ? (
                  <>
                    <Input
                      data-testid={`edit-category-input-${c.id}`}
                      value={editingName}
                      autoFocus
                      onChange={(e) => setEditingName(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleRename(c.id);
                        }
                      }}
                    />
                    <button
                      data-testid={`save-category-${c.id}`}
                      onClick={() => handleRename(c.id)}
                      disabled={busyId === c.id}
                      className="p-1.5 rounded-sm text-emerald-400 hover:bg-zinc-800"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName("");
                      }}
                      className="p-1.5 rounded-sm text-zinc-500 hover:bg-zinc-800"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100">
                        {c.name}
                        {c.is_default && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            default
                          </span>
                        )}
                        {c.is_archived && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500"
                            data-testid={`category-archived-${c.id}`}
                          >
                            <Archive className="w-3 h-3" /> archived
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid={`edit-category-${c.id}`}
                      onClick={() => {
                        setEditingId(c.id);
                        setEditingName(c.name);
                      }}
                      className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`delete-category-${c.id}`}
                      onClick={() => handleDelete(c)}
                      disabled={busyId === c.id}
                      className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-800"
                    >
                      {busyId === c.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </>
                )}
              </li>
            ))
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
